import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { ChevronRight } from "lucide-react";
import { Helmet } from "react-helmet-async";

/**
 * Breadcrumb Navigation Component
 * Displays the current page path and generates Schema.org BreadcrumbList for SEO
 */

interface BreadcrumbItem {
  label: string;
  path: string;
}

const BREADCRUMB_MAP: Record<string, string> = {
  "/": "Startseite",
  "/ueber-mich": "Über mich",
  "/finanzierung": "Finanzierung",
  "/erbschaft": "Erbschaft",
  "/immobilien": "Immobilien",
  "/immobilienbewertung": "Immobilienbewertung",
  "/umziehen-in-heilbronn": "Umziehen in Heilbronn",
  "/leistungen": "Leistungen",
  "/team": "Team",
  "/kontakt": "Kontakt",
  "/blog": "Blog",
  "/faq": "FAQ",
  "/kampagne": "Kampagne",
  "/impressum": "Impressum",
  "/datenschutz": "Datenschutz",
};

export default function Breadcrumb() {
  const [location] = useLocation();
  // React 19 hoistiert Helmet-Script-Tags im Browser in den Head. Beim SSR
  // werden sie dagegen aus dem App-HTML extrahiert. Die kurze Verzögerung
  // verhindert deshalb eine Script-/Sibling-Abweichung im Hydration-Erstrender.
  const [helmetReady, setHelmetReady] = useState(typeof window === "undefined");
  useEffect(() => setHelmetReady(true), []);

  // Generate breadcrumb items
  const getBreadcrumbs = (): BreadcrumbItem[] => {
    // Always start with home
    const breadcrumbs: BreadcrumbItem[] = [{ label: "Startseite", path: "/" }];

    // Don't add duplicate if already on home
    if (location === "/") {
      return breadcrumbs;
    }

    // Handle blog post detail pages
    if (location.startsWith("/blog/")) {
      breadcrumbs.push({ label: "Blog", path: "/blog" });
      const slug = location.replace("/blog/", "");
      const title = decodeURIComponent(slug)
        .replace(/-/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      breadcrumbs.push({ label: title, path: location });
      return breadcrumbs;
    }

    // Handle immobilien detail pages
    if (location.startsWith("/immobilien/")) {
      breadcrumbs.push({ label: "Immobilien", path: "/immobilien" });
      breadcrumbs.push({ label: "Objektdetails", path: location });
      return breadcrumbs;
    }

    // Handle other routes
    const label = BREADCRUMB_MAP[location];
    if (label) {
      breadcrumbs.push({ label, path: location });
    }

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  // Generate Schema.org BreadcrumbList
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.label,
      item: `https://immozwerg.com${item.path}`,
    })),
  };

  // Inject JSON-LD via Helmet for SSR compatibility
  // This ensures the schema is available to crawlers during SSR

  // Die Finanzierungsseite zeigt die sichtbare Breadcrumb-Zeile oberhalb des
  // gemeinsamen Headers. Blog und Wertberechnung folgen derselben Struktur;
  // nur auf der Startseite entfällt die zusätzliche Orientierungsebene.
  const hideVisualBreadcrumb = location === "/";

  return (
    <>
      {helmetReady && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
          </script>
        </Helmet>
      )}
    {!hideVisualBreadcrumb && <nav className="bg-white/95 backdrop-blur-sm shadow-sm sticky top-0 z-40 border-b border-gray-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 h-12 text-sm">
          {breadcrumbs.map((item, index) => (
            <div key={item.path} className="flex items-center gap-2">
              <a
                href={item.path}
                className={`transition-colors hover:text-[#631172] ${
                  index === breadcrumbs.length - 1
                    ? "text-[#631172] font-medium"
                    : "text-gray-600"
                }`}
              >
                {item.label}
              </a>
              {index < breadcrumbs.length - 1 && (
                <ChevronRight size={16} className="text-gray-300" />
              )}
            </div>
          ))}
        </div>
      </div>
    </nav>}
    </>
  );
}
